class LookSliderShopTheLook extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.init();

    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', (event) => {
        if (event.target.contains(this)) {
          this.init();
        }
      }, { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.sliderAbortController?.abort();
  }

  init() {
    const slider = this.querySelector('.look-slider-shop-the-look__swiper');

    if (!slider || typeof Swiper === 'undefined') {
      return;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    this.sliderAbortController?.abort();
    this.sliderAbortController = new AbortController();

    const startIndex = Math.max(Number(this.dataset.startIndex || 0), 0);
    const centeredSlides = this.dataset.centeredSlides === 'true';
    const prevEl = this.querySelector('.look-slider-shop-the-look__button--prev');
    const nextEl = this.querySelector('.look-slider-shop-the-look__button--next');

    this.swiper = new Swiper(slider, {
      a11y: false,
      grabCursor: true,
      slidesPerView: 'auto',
      spaceBetween: this.getSlideGap(),
      initialSlide: startIndex,
      centeredSlides,
      preventClicks: false,
      preventClicksPropagation: false,
      noSwipingSelector: '.look-slider-shop-the-look__dot, .look-slider-shop-the-look__product-card',
      resistanceRatio: 0.72,
      watchSlidesProgress: true,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: true,
        sensitivity: 1,
      },
      navigation: {
        prevEl,
        nextEl,
      },
      breakpoints: {
        750: {
          spaceBetween: this.getSlideGap(),
        },
      },
    });

    this.setSlideLabels();
    this.bindWheelFallback(slider);
    this.bindQuickCartTriggers();
  }

  getSlideGap() {
    const gapValue = getComputedStyle(this).getPropertyValue('--look-slider-gap').trim();
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;

    if (gapValue.endsWith('rem')) {
      return Number.parseFloat(gapValue) * rootFontSize;
    }

    if (gapValue.endsWith('px')) {
      return Number.parseFloat(gapValue);
    }

    return Number.parseFloat(gapValue) || 0;
  }

  setSlideLabels() {
    if (!this.swiper) {
      return;
    }

    const lookWording = this.dataset.lookWording || 'Look n°';
    const wrapperId = `look-slider-wrapper-${Math.random().toString(36).slice(2)}`;

    this.swiper.wrapperEl.id = wrapperId;
    this.swiper.wrapperEl.setAttribute('aria-live', 'polite');
    this.swiper.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `${lookWording} ${index + 1}`);
      slide.dataset.index = index;
    });

    [this.swiper.navigation?.prevEl, this.swiper.navigation?.nextEl].flat().filter(Boolean).forEach((button) => {
      button.setAttribute('aria-controls', wrapperId);
    });
  }

  bindWheelFallback(slider) {
    this.wheelDelta = 0;
    this.wheelLocked = false;

    slider.addEventListener('wheel', (event) => {
      if (!this.swiper || this.swiper.destroyed) {
        return;
      }

      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 3) {
        return;
      }

      const delta = event.deltaX;
      const movingBack = delta < 0;
      const movingForward = delta > 0;
      const atStart = this.swiper.isBeginning && movingBack;
      const atEnd = this.swiper.isEnd && movingForward;

      if (atStart || atEnd || Math.abs(delta) < 3) {
        return;
      }

      event.preventDefault();

      if (this.wheelLocked) {
        return;
      }

      this.wheelDelta += delta;

      if (Math.abs(this.wheelDelta) < 45) {
        return;
      }

      if (this.wheelDelta > 0) {
        this.swiper.slideNext(320);
      } else {
        this.swiper.slidePrev(320);
      }

      this.wheelDelta = 0;
      this.wheelLocked = true;

      window.setTimeout(() => {
        this.wheelLocked = false;
      }, 260);
    }, { passive: false, signal: this.sliderAbortController.signal });
  }

  bindQuickCartTriggers() {
    const bind = () => {
      const drawer = document.querySelector('quick-cart-drawer');

      if (!drawer || typeof drawer.fetchProductForQuickCartDrawer !== 'function') {
        return;
      }

      this.querySelectorAll('.quick-cart-drawer__trigger').forEach((trigger) => {
        if (trigger._lookSliderQuickCartInit) {
          return;
        }

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          drawer.fetchProductForQuickCartDrawer({ target: trigger });
        }, { capture: true });
        trigger._lookSliderQuickCartInit = true;
      });
    };

    bind();

    if (customElements.whenDefined) {
      customElements.whenDefined('quick-cart-drawer').then(bind);
    }
  }
}

if (!customElements.get('look-slider-shop-the-look')) {
  customElements.define('look-slider-shop-the-look', LookSliderShopTheLook);
}
